import { ChangeDetectionStrategy, Component, forwardRef, input, OnChanges, output, signal, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectionOption } from '../../entity/utility-interfaces';

@Component({
    selector: 'app-single-select-dropdown',
    templateUrl: './single-select-dropdown.component.html',
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => SingleSelectDropdownComponent),
        multi: true
      }
    ],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SingleSelectDropdownComponent implements ControlValueAccessor, OnChanges {

  readonly data = input<SelectionOption[]>([]);
  readonly selectionChange = output<string | undefined>();
  readonly placeholder = input<string>();
  readonly className = input<string>();
  readonly width = input<string>();
  readonly height = input<string | undefined>('32');
  readonly isDisabled = input(false);
  readonly showClear = input(false);
  readonly selectedValue = signal<string | undefined>(undefined);
  readonly disabledState = signal(false);
  private onChange: (value: string | undefined) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  /**
   * Setter for the selection option
   */
  readonly selectedOption = input<any>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedOption']) {
      const value = this.selectedOption();
      this.selectedValue.set(this.normalizeSelectedValue(value?.id ?? value));
    }
    if (changes['isDisabled']) {
      this.disabledState.set(this.isDisabled());
    }
  }

  constructor() {
  }

  /**
   * ControlValueAccessor hook. Called by Angular forms to push a value into the
   * component. Angular may pass `null` to clear the control, which is coerced to
   * `undefined` for internal consistency.
   * @param value The option `id` to select, or `null`/`undefined` to clear.
   */
  writeValue(value: string | null | undefined): void {
    // Angular may pass null to clear the control; coerce to undefined for internal consistency.
    this.selectedValue.set(value != null ? value : undefined);
  }

  /**
   * ControlValueAccessor hook. Registers the callback used to notify the form
   * model when the selected value changes.
   * @param fn Callback invoked with the selected option `id` (or `undefined`).
   */
  registerOnChange(fn: (value: string | undefined) => void): void {
    this.onChange = fn;
  }

  /**
   * ControlValueAccessor hook. Registers the callback used to mark the control
   * as touched when the user interacts with the dropdown.
   * @param fn Callback invoked on first interaction.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * ControlValueAccessor hook. Enables or disables the dropdown in response to
   * the associated form control's disabled state.
   * @param isDisabled `true` to disable the control, `false` to enable it.
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabledState.set(isDisabled);
  }

  /**
   * Handles the PrimeNG select change. Normalizes the value, propagates it to
   * the form model via the CVA callbacks, and emits `selectionChange` for
   * backward compatibility with existing consumers.
   */
  onClickSelectionChange() {
    const selectedValue = this.normalizeSelectedValue(this.selectedValue());
    this.selectedValue.set(selectedValue);
    this.onChange(selectedValue);
    this.onTouched();
    this.selectionChange.emit(selectedValue);
  }

  /**
   * Normalizes a raw value to a defined option `id` or `undefined`, so empty
   * selections consistently clear the control (enabling `Validators.required`
   * to flag the field as invalid).
   * @param value The raw value to normalize.
   * @returns The option `id` string, or `undefined` when empty.
   */
  private normalizeSelectedValue(value: string | null | undefined): string | undefined {
    return value != null ? value : undefined;
  }

}
